import { AnimatePresence, motion } from 'framer-motion'
import { array, bool, func, node, object, string } from 'prop-types'
import { useRef, useEffect } from 'react'
import { componentWillAppendToBody } from 'react-append-to-body'
import { v4 as uuidv4 } from 'uuid'
import { getTrackableComponent } from '@opallabs/quanta'
import Alert from '../Alert'
import { UnwrappedButton } from '../Button'
import Conditional from '../Conditional'
import HotKey from '../HotKey'
import { kuiperClassNames } from '../utils'
import './styles.scss'

const key = uuidv4()

const ModalBody = (props) => {
  const {
    cancelExitButtonText,
    children,
    classes = [],
    modalHint,
    closeOnOverlayClick = false,
    confirmExitButtonText,
    confirmExitMessage,
    confirmExitTitle,
    customClassname,
    displayExitConfirmation = false,
    emotionStyleOverrides,
    headerContents,
    onCancelExit,
    onClose,
    shouldConfirmExit,
    onDisplayConfirmation = () => {},
    displayExitButton = true,
    trackers = {},
    theme = 'dark',
    visible = false,
  } = props

  const overlayRef = useRef(null)
  const headerRef = useRef(null)
  const bodyRef = useRef(null)
  const hintRef = useRef(null)
  const wasVisible = useRef(false)

  useEffect(() => {
    if (visible) {
      wasVisible.current = true
      if (typeof window.hideSelector !== 'undefined')
        window.hideSelector('#body_wrapper')
      if (typeof window.hideSelector !== 'undefined')
        window.hideSelector('#base_wrapper')
      return
    }

    if (!wasVisible.current) return
    wasVisible.current = false

    if (typeof window.showSelector !== 'undefined')
      window.showSelector('#body_wrapper')
    if (typeof window.showSelector !== 'undefined')
      window.showSelector('#base_wrapper')
  }, [visible])

  useEffect(
    () => () => {
      if (typeof window.showSelector !== 'undefined')
        window.showSelector('#body_wrapper')
      if (typeof window.showSelector !== 'undefined')
        window.showSelector('#base_wrapper')
    },
    []
  )

  const closeOrConfirm = (e, trackingFn) => {
    if (!visible) return

    if (shouldConfirmExit) {
      onDisplayConfirmation(e, trackingFn)
    } else {
      onClose(e, trackingFn)
    }
  }

  const handleEscHotkey = (e) => {
    const trackingFn = trackers && trackers.hotkey
    closeOrConfirm(e, trackingFn)
  }

  const handleOverlayClick = (e) => {
    const trackingFn = trackers && trackers.click

    if (!closeOnOverlayClick) return

    const refMatchesTarget = (ref) =>
      ref && ref.current && ref.current.isEqualNode(e.target)
    const refContainsTarget = (ref) =>
      ref && ref.current && ref.current.contains(e.target)

    // Clicking modal hint should close modal
    if (refContainsTarget(hintRef)) {
      closeOrConfirm(e, trackingFn)
      return
    }

    /**
     * Click events will bubble up from the modal contents
     * and be caught by ModalOverlay's onClick event.
     * This confirms that the click happened on a portion
     * of the overlay, and not the modal contents.
     */
    const clickTargetIsOverlay = [overlayRef, headerRef, bodyRef].some(
      refMatchesTarget
    )

    if (clickTargetIsOverlay) {
      closeOrConfirm(e, trackingFn)
    }
  }

  const modalClasses = kuiperClassNames(
    'modal',
    { [customClassname]: customClassname },
    theme,
    ...classes
  )

  return (
    <AnimatePresence>
      {visible && (
        /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
        <motion.div
          css={{ position: 'relative', zIndex: 1998 }}
          transition={{ duration: 0.15 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            css={emotionStyleOverrides}
            className={modalClasses}
            onClick={handleOverlayClick}
            key={key}
            ref={overlayRef}
          >
            <HotKey hotKey="esc" callback={handleEscHotkey} />
            <div className={kuiperClassNames('modal-header')} ref={headerRef}>
              <Conditional condition={displayExitButton}>
                <UnwrappedButton
                  className={kuiperClassNames('modal-close')}
                  onClick={closeOrConfirm}
                  position="left"
                  label="Close"
                  noArrow={true}
                  hideLabelText={true}
                  size="medium"
                  type="invisible"
                  icon="close-sm"
                />
              </Conditional>
              {headerContents}
            </div>
            <div className={kuiperClassNames('modal-body')} ref={bodyRef}>
              {children}
              {modalHint && (
                <div className={kuiperClassNames('modal-hint')} ref={hintRef}>
                  {modalHint}
                </div>
              )}
            </div>
            <Conditional
              condition={shouldConfirmExit && displayExitConfirmation}
            >
              <div
                className={kuiperClassNames('alert-wrapper', 'bg-color-white')}
              >
                <Alert
                  className="bounce-in"
                  message={confirmExitMessage}
                  hideBody={true}
                  title={confirmExitTitle}
                >
                  <UnwrappedButton
                    className="cancel"
                    label={cancelExitButtonText}
                    type="nobutton"
                    onClick={onCancelExit}
                  />
                  <UnwrappedButton
                    className="confirm"
                    label={confirmExitButtonText}
                    onClick={onClose}
                  />
                </Alert>
              </div>
            </Conditional>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

ModalBody.propTypes = {
  cancelExitButtonText: string,
  children: node,
  classes: array,
  closeOnOverlayClick: bool,
  confirmExitButtonText: string,
  confirmExitMessage: string,
  confirmExitTitle: string,
  customClassname: string,
  displayExitButton: bool,
  displayExitConfirmation: bool,
  emotionStyleOverrides: object,
  headerContents: node,
  modalHint: node,
  onCancelExit: func,
  onClose: func.isRequired,
  onDisplayConfirmation: func,
  shouldConfirmExit: bool,
  theme: string,
  trackers: object,
  visible: bool,
}

const UnmanagedModal = componentWillAppendToBody(ModalBody)
export const TrackedUnmanagedModal = getTrackableComponent(
  UnmanagedModal,
  'Modal'
)
export default UnmanagedModal
